import { type FlexProps } from "@chakra-ui/react";
import { type TzktCombinedOperation } from "@umami/chains";
import { fromRawToken } from "@umami/core";
import { useGetTokenTransfer } from "@umami/state";
import { memo } from "react";

import { DelegationTile } from "./DelegationTile";
import { FinalizeUnstakeTile } from "./FinalizeUnstakeTile";
import { OriginationTile } from "./OriginationTile";
import { SafeSendTxTile } from "./SafeSendTxTile";
import { StakeTile } from "./StakeTile";
import { TokenTransferTile } from "./TokenTransferTile";
import { TransactionTile } from "./TransactionTile";
import { UnstakeTile } from "./UnstakeTile";

export const OperationTile = memo(
  ({ operation, ...props }: { operation: TzktCombinedOperation } & FlexProps) => {
    const getTokenTransfer = useGetTokenTransfer();
    switch (operation.type) {
      case "token_transfer": {
        const token = fromRawToken(operation.token);

        if (token) {
          return <TokenTransferTile token={token} tokenTransfer={operation} {...props} />;
        }
        console.warn(`Could not parse token transfer ${operation.id}`);

        return null;
      }

      case "safe_send":
        return <SafeSendTxTile operation={operation} {...props} />;

      case "transaction": {
        const tokenTransfer = getTokenTransfer(operation.id);

        if (tokenTransfer) {
          const token = fromRawToken(tokenTransfer.token);

          if (token) {
            return (
              <TokenTransferTile
                operation={operation}
                token={token}
                tokenTransfer={tokenTransfer}
                {...props}
              />
            );
          } else {
            // If we can't parse the token we fallback to
            // the transaction tile because it is a transaction by nature
            return <TransactionTile operation={operation} {...props} />;
          }
        } else {
          return <TransactionTile operation={operation} {...props} />;
        }
      }
      case "delegation":
        return <DelegationTile operation={operation} {...props} />;

      case "origination":
        return <OriginationTile operation={operation} {...props} />;

      case "stake":
        return <StakeTile operation={operation} {...props} />;

      case "unstake":
        return <UnstakeTile operation={operation} {...props} />;

      case "finalize":
        return <FinalizeUnstakeTile operation={operation} {...props} />;
    }
  }
);
